import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { useCreateMember, useUpdateMember } from '../api';
import type { User, CoCompany } from '@/lib/api/types';
import type { AccessRole } from '@/lib/api/accessManager';
import { useAuthStore } from '@/lib/auth/authStore';
import { isPlanLimitError } from '@/lib/permissions/quotaError';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: User | null;
  companies: CoCompany[];
  roles: AccessRole[];
  /** Fired when backend rejects with PLAN_LIMIT_EXCEEDED — callers open
   * UpgradeDialog instead of showing a raw error toast. */
  onQuotaExceeded?: () => void;
}

export function MemberFormDialog({ open, onOpenChange, editing, companies, roles, onQuotaExceeded }: Props) {
  const t = useT();
  const create = useCreateMember();
  const update = useUpdateMember();
  const sessionApp = useAuthStore((s) => s.app);
  // Members are created inside the manager's own company. Sub-brands and
  // child agencies have their own managers — this one only manages here.
  const managerCompanyId = useAuthStore((s) => s.user?.companyId) ?? 0;
  const managerCompanyName =
    companies.find((c) => c.id === managerCompanyId)?.name ?? 'your workspace';

  const [form, setForm] = useState({
    email: '',
    firstname: '',
    lastname: '',
    phone: '',
    password: '',
    roleId: 0,
  });

  useEffect(() => {
    if (editing) {
      setForm({
        email: editing.email,
        firstname: editing.firstname ?? '',
        lastname: editing.lastname ?? '',
        phone: editing.phone ?? '',
        password: '',
        roleId:
          typeof editing.role === 'object' && editing.role
            ? editing.role.id
            : 0,
      });
    } else {
      setForm({
        email: '',
        firstname: '',
        lastname: '',
        phone: '',
        password: '',
        roleId: 0,
      });
    }
  }, [editing]);

  const isEditing = !!editing;
  const isSaving = create.isPending || update.isPending;

  function submit() {
    if (!form.email || !form.firstname || !form.lastname) {
      toast({ tone: 'error', title: t('memberForm.errRequired') });
      return;
    }
    if (!managerCompanyId) {
      toast({ tone: 'error', title: t('memberForm.errNoWorkspace') });
      return;
    }

    if (isEditing) {
      update.mutate(
        {
          id: editing!.id,
          body: {
            firstname: form.firstname,
            lastname: form.lastname,
            phone: form.phone || undefined,
            roleId: form.roleId || undefined,
          },
        },
        {
          onSuccess: () => {
            toast({ tone: 'success', title: t('memberForm.updated') });
            onOpenChange(false);
          },
          onError: (e: Error) => toast({ tone: 'error', title: e.message }),
        },
      );
      return;
    }

    create.mutate(
      {
        email: form.email,
        firstname: form.firstname,
        lastname: form.lastname,
        phone: form.phone || undefined,
        password: form.password || undefined,
        companyId: managerCompanyId,
        roleId: form.roleId || undefined,
        appType: sessionApp ?? 'xotski',
      },
      {
        onSuccess: (res) => {
          toast({
            tone: 'success',
            title: res.created ? t('memberForm.created') : t('memberForm.existed'),
          });
          onOpenChange(false);
        },
        onError: (e: Error) => {
          if (isPlanLimitError(e) && onQuotaExceeded) {
            onOpenChange(false);
            onQuotaExceeded();
            return;
          }
          toast({ tone: 'error', title: e.message });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? t('memberForm.titleEdit') : t('memberForm.titleAdd')}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Update details for ${editing!.email}`
              : `New members are created in ${managerCompanyName}. The role you pick decides which company subtree they can actually see.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1.5">
              <span className="text-xs font-semibold text-[var(--color-fg-muted)]">{t('memberForm.firstName')}</span>
              <Input
                value={form.firstname}
                onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                placeholder="Ada"
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-semibold text-[var(--color-fg-muted)]">{t('memberForm.lastName')}</span>
              <Input
                value={form.lastname}
                onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                placeholder="Lovelace"
              />
            </label>
          </div>

          <label className="space-y-1.5 block">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">{t('memberForm.email')}</span>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={isEditing}
              placeholder="ada@example.com"
            />
          </label>

          <label className="space-y-1.5 block">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">{t('memberForm.phone')}</span>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 555 0100"
            />
          </label>

          {!isEditing && (
            <label className="space-y-1.5 block">
              <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
                Initial password (optional — defaults to system invitation flow)
              </span>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
              />
            </label>
          )}

          <label className="space-y-1.5 block">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">{t('memberForm.role')}</span>
            <Select
              value={String(form.roleId)}
              onChange={(e) => setForm({ ...form, roleId: Number(e.target.value) })}
            >
              <option value="0">{t('memberForm.noRole')}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.companyId == null
                    ? ' (template)'
                    : r.company?.name
                      ? ` — scope: ${r.company.name}`
                      : ''}
                </option>
              ))}
            </Select>
            <span className="text-[10px] text-[var(--color-fg-subtle)]">
              The role's company scope is what restricts this user's visibility on login.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSaving}>
            {isEditing ? t('memberForm.submitSave') : t('memberForm.submitCreate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
